import React, { useState, FC, useRef, RefObject } from 'react';
import {
    Button, Typography, Dialog, DialogContent,
    DialogTitle, DialogActions, Paper, IconButton,
    Box, CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

interface DeleteProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    selectedRecord: any;
    reloading: () => void;
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const DeleteAccrualDate: FC<DeleteProps> = ({ open, setOpen, selectedRecord, reloading }) => {

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);

    const closeForm = () => {
        setOpen(false);
        setLoading(false);
    };

    const handleDelete = async () => {
        setLoading(true);

        // Simular proceso de eliminación
        setTimeout(() => {
            enqueueSnackbar(t('accruals.accrual_dates.dialog.alerts.delete.success'), { variant: 'success' });
            closeForm();
            reloading();
        }, 1500);
    };

    return (
        <Dialog
            open={open}
            onClose={() => !loading && closeForm()}
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.accrual_dates.dialog.titles.delete')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={() => !loading && closeForm()}
                disabled={loading}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <WarningAmberIcon color="warning" sx={{ fontSize: 48 }} />
                    <Box>
                        <Typography variant="body1" color="text.secondary">
                            {t('accruals.accrual_dates.dialog.alerts.delete.message')}
                        </Typography>
                    </Box>
                </Box>

                {selectedRecord && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('accruals.accrual_dates.table.columns.name')}:
                        </Typography>
                        <Typography variant="body1" fontWeight="medium">
                            {selectedRecord.name}
                        </Typography>
                    </Box>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2">
                            {'eliminando...'}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="inherit"
                    onClick={closeForm}
                    disabled={loading}
                >
                    {t('general.buttons.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleDelete}
                    disabled={loading}
                >
                    {t('general.buttons.delete')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { DeleteAccrualDate };
